import React from 'react';
import ReactDOM from 'react-dom';
import { DataProvider, useData, useField } from './EpcrDataContext';
import { Icons } from './EpcrIcons';
import { EpcrViewIncident } from './views/EpcrViewIncident';
import { EpcrViewPatient } from './views/EpcrViewPatient';
import { EpcrViewVitals } from './views/EpcrViewVitals';
import { EpcrViewFlowchart } from './views/EpcrViewFlowchart';
import { EpcrViewAssessments } from './views/EpcrViewAssessments';
import { EpcrViewNarrative } from './views/EpcrViewNarrative';
import { EpcrViewBilling, EpcrViewSignatures } from './views/EpcrViewRest';
import { EpcrViewRaw } from './views/EpcrViewRaw';
import './EpcrPage.css';

const URLS = require('../../../urls.js');

const VIEWS = [
  { id: "incident",    label: "Incident",    icon: Icons.truck },
  { id: "patient",     label: "Patient",     icon: Icons.user },
  { id: "vitals",      label: "Vitals",      icon: Icons.pulse },
  { id: "flowchart",   label: "Flowchart",   icon: Icons.flow },
  { id: "assessments", label: "Assessments", icon: Icons.clipboard },
  { id: "narrative",   label: "Narrative",   icon: Icons.note },
  { id: "billing",     label: "Billing",     icon: Icons.dollar },
  { id: "signatures",  label: "Signatures",  icon: Icons.pen },
  { id: "raw",         label: "Raw",         icon: Icons.list },
];

const VIEW_COMPONENTS = {
  incident:    EpcrViewIncident,
  patient:     EpcrViewPatient,
  vitals:      EpcrViewVitals,
  flowchart:   EpcrViewFlowchart,
  assessments: EpcrViewAssessments,
  narrative:   EpcrViewNarrative,
  billing:     EpcrViewBilling,
  signatures:  EpcrViewSignatures,
  raw:         EpcrViewRaw,
};

// Maps NEMSIS element numbers to the view and section that contain them.
// Prefix-based fallback handles any element not explicitly listed.
const FIELD_MAP = {
  // incident — response
  'eRecord.01':    { view: 'incident', section: 'response' },
  'eResponse.03':  { view: 'incident', section: 'response' },
  'eResponse.04':  { view: 'incident', section: 'response' },
  'eResponse.05':  { view: 'incident', section: 'response' },
  'eResponse.07':  { view: 'incident', section: 'response' },
  'eResponse.08':  { view: 'incident', section: 'response' },
  'eResponse.09':  { view: 'incident', section: 'response' },
  'eResponse.13':  { view: 'incident', section: 'response' },
  'eResponse.14':  { view: 'incident', section: 'response' },
  'eResponse.23':  { view: 'incident', section: 'response' },
  'eResponse.24':  { view: 'incident', section: 'response' },
  // incident — scene
  'eScene.06':     { view: 'incident', section: 'scene' },
  'eScene.07':     { view: 'incident', section: 'scene' },
  'eScene.09':     { view: 'incident', section: 'scene' },
  'eScene.11':     { view: 'incident', section: 'scene' },
  'eScene.15':     { view: 'incident', section: 'scene' },
  'eScene.17':     { view: 'incident', section: 'scene' },
  'eScene.18':     { view: 'incident', section: 'scene' },
  'eScene.19':     { view: 'incident', section: 'scene' },
  'eScene.21':     { view: 'incident', section: 'scene' },
  // incident — times
  'eTimes.01':     { view: 'incident', section: 'times' },
  'eTimes.02':     { view: 'incident', section: 'times' },
  'eTimes.03':     { view: 'incident', section: 'times' },
  'eTimes.05':     { view: 'incident', section: 'times' },
  'eTimes.06':     { view: 'incident', section: 'times' },
  'eTimes.07':     { view: 'incident', section: 'times' },
  'eTimes.09':     { view: 'incident', section: 'times' },
  'eTimes.11':     { view: 'incident', section: 'times' },
  'eTimes.13':     { view: 'incident', section: 'times' },
  // incident — disposition
  'eDisposition.27': { view: 'incident', section: 'disposition' },
  'eDisposition.13': { view: 'incident', section: 'disposition' },
  'eDisposition.14': { view: 'incident', section: 'disposition' },
  'eDisposition.16': { view: 'incident', section: 'disposition' },
  'eDisposition.17': { view: 'incident', section: 'disposition' },
  'eDisposition.19': { view: 'incident', section: 'disposition' },
  'eDisposition.20': { view: 'incident', section: 'destination' },
  'eDisposition.21': { view: 'incident', section: 'destination' },
  'eDisposition.28': { view: 'incident', section: 'disposition' },
  'eDisposition.29': { view: 'incident', section: 'disposition' },
  'eDisposition.30': { view: 'incident', section: 'disposition' },
  'eDisposition.31': { view: 'incident', section: 'disposition' },
  'eDisposition.32': { view: 'incident', section: 'disposition' },
  // patient — demographics
  'ePatient.02':   { view: 'patient', section: 'demographics' },
  'ePatient.03':   { view: 'patient', section: 'demographics' },
  'ePatient.04':   { view: 'patient', section: 'demographics' },
  'ePatient.12':   { view: 'patient', section: 'demographics' },
  'ePatient.14':   { view: 'patient', section: 'demographics' },
  'ePatient.15':   { view: 'patient', section: 'demographics' },
  'ePatient.16':   { view: 'patient', section: 'demographics' },
  'ePatient.17':   { view: 'patient', section: 'demographics' },
  'ePatient.24':   { view: 'patient', section: 'demographics' },
  'ePatient.25':   { view: 'patient', section: 'demographics' },
  // patient — contact
  'ePatient.05':   { view: 'patient', section: 'contact' },
  'ePatient.06':   { view: 'patient', section: 'contact' },
  'ePatient.07':   { view: 'patient', section: 'contact' },
  'ePatient.08':   { view: 'patient', section: 'contact' },
  'ePatient.09':   { view: 'patient', section: 'contact' },
  'ePatient.10':   { view: 'patient', section: 'contact' },
  'ePatient.18':   { view: 'patient', section: 'contact' },
  'ePatient.19':   { view: 'patient', section: 'contact' },
  // patient — history / allergies
  'eHistory.06':   { view: 'patient', section: 'allergies' },
  'eHistory.07':   { view: 'patient', section: 'allergies' },
  'eHistory.08':   { view: 'patient', section: 'history' },
  'eHistory.13':   { view: 'patient', section: 'history' },
  // narrative
  'eSituation.01': { view: 'narrative', section: 'times' },
  'eSituation.03': { view: 'narrative', section: 'complaint' },
  'eSituation.04': { view: 'narrative', section: 'complaint' },
  'eSituation.05': { view: 'narrative', section: 'complaint' },
  'eSituation.06': { view: 'narrative', section: 'complaint' },
  'eSituation.07': { view: 'narrative', section: 'complaint' },
  'eSituation.09': { view: 'narrative', section: 'impression' },
  'eSituation.10': { view: 'narrative', section: 'signs' },
  'eSituation.11': { view: 'narrative', section: 'impression' },
  'eSituation.12': { view: 'narrative', section: 'impression' },
  'eSituation.18': { view: 'narrative', section: 'times' },
  'eNarrative.01': { view: 'narrative', section: 'narrative' },
  'eInjury.01':    { view: 'narrative', section: 'injuries' },
  'eInjury.03':    { view: 'narrative', section: 'injuries' },
};

const VIEW_BY_PREFIX = {
  eRecord: 'incident', eResponse: 'incident', eTimes: 'incident',
  eScene: 'incident', eDisposition: 'incident', eCrew: 'incident',
  ePatient: 'patient', eHistory: 'patient',
  eVitals: 'vitals',
  eProcedures: 'flowchart', eMedications: 'flowchart',
  eSituation: 'narrative', eNarrative: 'narrative', eInjury: 'narrative',
  eExam: 'assessments',
};

function getFieldLocation(elemNumber) {
  if (!elemNumber) return null;
  if (FIELD_MAP[elemNumber]) return FIELD_MAP[elemNumber];
  const prefix = elemNumber.split('.')[0];
  const view = VIEW_BY_PREFIX[prefix];
  return view ? { view, section: null } : null;
}

const REPORTS = [
  { file: "report_empty.json",       label: "2026-demo-01",   sublabel: "Blank Report" },
  { file: "report.json",             label: "2026-demo-02",   sublabel: "Seizure" },
];

export default function EpcrPage() {
  return (
    <DataProvider>
      <EpcrApp />
    </DataProvider>
  );
}

// ── 7A helpers: path resolution and op application ──────────────────────────

function mutationKey(r) {
  return `${r.feedbackTarget}:${r.elem}:${r.op}:${r.value}:${r.test_metadata_id}`;
}

function promoteToMutation(proto, data) {
  const path = (proto.elem ? data.path(proto.elem) : null) || null;
  const groupPath = (path && path.includes('Group'))
    ? path.split('/').slice(0, -1).join('/')
    : null;
  return { ...proto, path, groupPath };
}

function readAtPath(report, path) {
  try {
    let obj = report.EMSDataSet.Header.PatientCareReport;
    for (const seg of path.split('/')) {
      if (obj == null) return undefined;
      obj = obj[seg];
    }
    return obj;
  } catch { return undefined; }
}

function applyMutation(proto, data) {
  if (!proto.elem || !proto.op) return false;
  const m = promoteToMutation(proto, data);
  if (!m.path) {
    console.warn(`[applyMutation] no path for elem=${proto.elem}`);
    return false;
  }
  const { op, value, path, groupPath } = m;
  const current = readAtPath(data.report, path);

  switch (op) {
    case 'set':
    case 'clear':
      data.setReportPath(path, value ?? null);
      break;
    case 'replace':
      if (Array.isArray(current)) {
        data.setReportPath(path, current.map(v => v === proto.removeValue ? value : v));
      } else {
        data.setReportPath(path, value);
      }
      break;
    case 'add': {
      const arr = Array.isArray(current) ? current : (current != null ? [current] : []);
      data.setReportPath(path, [...arr, value]);
      break;
    }
    case 'add_group': {
      if (!groupPath) { console.warn('[applyMutation] add_group but no groupPath'); return false; }
      const groupCurrent = readAtPath(data.report, groupPath);
      const arr = Array.isArray(groupCurrent) ? groupCurrent : (groupCurrent != null ? [groupCurrent] : []);
      const newGroup = proto.template || (value != null ? { [proto.elem]: value } : {});
      data.setReportPath(groupPath, [...arr, newGroup]);
      break;
    }
    default:
      console.warn(`[applyMutation] unknown op=${op}`);
      return false;
  }
  console.log(`[applyMutation] op=${op} elem=${proto.elem} path=${path} value=${value}`);
  return true;
}

function EpcrApp() {
  const [view, setView]                 = React.useState("incident");
  const [rapidOpen, setRapidOpen]       = React.useState(false);
  const [reportOpen, setReportOpen]     = React.useState(false);
  const [validationOpen, setValidationOpen] = React.useState(false);
  const [validating, setValidating]     = React.useState(false);
  const [violations, setViolations]     = React.useState(null);
  const [sending, setSending]                         = React.useState(false);
  const [dropdownResults, setDropdownResults]         = React.useState(null);
  const [schematronMutations, setSchematronMutations] = React.useState(null);
  const [rejectedIds, setRejectedIds]                 = React.useState([]);
  const [acceptedIds, setAcceptedIds]                 = React.useState([]);
  const data = useData();

  const handleSelectReport = React.useCallback(async (file) => {
    setReportOpen(false);
    setValidationOpen(false);
    setViolations(null);
    setValidating(false);
    setSending(false);
    setDropdownResults(null);
    setSchematronMutations(null);
    setRejectedIds([]);
    setAcceptedIds([]);
    await data.loadReport(file);
  }, [data]);

  const runValidation = React.useCallback(async () => {
    if (!data?.report) return;
    setValidating(true);
    setValidationOpen(true);
    try {
      const resp = await fetch(URLS.VALIDATE_EPCR_DRAFT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report }),
      });
      const result = await resp.json();
      setViolations(result.violations || []);
    } catch (e) {
      console.error('Validation error:', e);
      setViolations([]);
    } finally {
      setValidating(false);
    }
  }, [data]);

  const runSendToCadmus = React.useCallback(async () => {
    if (!data?.report) return;
    setSending(true);
    setValidationOpen(true);
    try {
      const resp = await fetch(URLS.SEND_TO_CADMUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report }),
      });
      const result = await resp.json();
      setViolations(result.violations || []);
      setDropdownResults(result.dropdownResults || []);
      setSchematronMutations(result.schematronMutations || []);
    } catch (e) {
      console.error('Send to CADMUS error:', e);
      setDropdownResults([]);
      setSchematronMutations([]);
    } finally {
      setSending(false);
    }
  }, [data]);

  const handleAccept = React.useCallback((r) => {
    applyMutation(r, data);
    setAcceptedIds(ids => [...ids, mutationKey(r)]);
  }, [data]);

  const handleReject = React.useCallback((r) => {
    setRejectedIds(ids => [...ids, mutationKey(r)]);
  }, []);

  const handleAcceptAll = React.useCallback(() => {
    const all = [
      ...(schematronMutations || []).filter(r => r.severity !== '[FATAL]'),
      ...(dropdownResults || []),
    ];
    const rejectedSet = new Set(rejectedIds);
    const toApply = all.filter(r => r.op !== null && !rejectedSet.has(mutationKey(r)));
    const newlyAccepted = [];
    for (const r of toApply) {
      applyMutation(r, data);
      newlyAccepted.push(mutationKey(r));
    }
    if (newlyAccepted.length > 0) {
      setAcceptedIds(ids => [...ids, ...newlyAccepted]);
    }
  }, [schematronMutations, dropdownResults, rejectedIds, data]);

  const navigateToViolation = React.useCallback((violation) => {
    const loc = getFieldLocation(violation.element);
    if (loc?.view) setView(loc.view);
    setValidationOpen(false);
    setTimeout(() => {
      const fieldEl = document.getElementById(`field-${violation.element}`);
      if (fieldEl) {
        fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldEl.classList.add('epcr-jump-flash');
        setTimeout(() => fieldEl.classList.remove('epcr-jump-flash'), 1500);
      } else if (loc?.section) {
        const sectionEl = document.querySelector(`[data-section="${loc.section}"]`);
        if (sectionEl) sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  }, []);

  if (!data?.loaded) {
    return (
      <div className="epcr-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, color: "var(--epcr-ink-3)" }}>
        <div className="loader-brand"><span className="r">CADMUS</span> ePCR</div>
        <div className="loader-bar" />
        <div style={{ fontSize: 12 }}>Loading enumerations & defined lists…</div>
      </div>
    );
  }

  const ViewComponent = VIEW_COMPONENTS[view];
  const totalCount = violations ? violations.length : 0;

  return (
    <div className="epcr-root">
      <TopToolbar
        onRapid={() => setRapidOpen(o => !o)}
        rapidOpen={rapidOpen}
        onCloseRapid={() => setRapidOpen(false)}
        onValidate={() => validationOpen ? setValidationOpen(false) : (violations !== null ? setValidationOpen(true) : runValidation())}
        validating={validating}
        violationCount={totalCount}
        hasViolations={violations !== null}
        reports={REPORTS}
        currentReport={data.currentReport}
        reportOpen={reportOpen}
        onToggleReport={() => setReportOpen(o => !o)}
        onCloseReport={() => setReportOpen(false)}
        onSelectReport={handleSelectReport}
      />
      <ViewTabs current={view} onSelect={setView} />
      <ViewComponent />
      {validationOpen && (
        <ValidationPanel
          violations={violations}
          validating={validating}
          totalCount={totalCount}
          onClose={() => setValidationOpen(false)}
          onRerun={runValidation}
          onNavigate={navigateToViolation}
          specFn={data.spec}
          sending={sending}
          dropdownResults={dropdownResults}
          schematronMutations={schematronMutations}
          rejectedIds={rejectedIds}
          acceptedIds={acceptedIds}
          onAccept={handleAccept}
          onReject={handleReject}
          onAcceptAll={handleAcceptAll}
          onSendToCadmus={runSendToCadmus}
        />
      )}
    </div>
  );
}

function TopToolbar({ onRapid, rapidOpen, onCloseRapid, onValidate, validating, violationCount, hasViolations,
                      reports, currentReport, reportOpen, onToggleReport, onCloseReport, onSelectReport }) {
  const last = useField("ePatient/ePatient.PatientNameGroup/ePatient.02");
  const first = useField("ePatient/ePatient.PatientNameGroup/ePatient.03");
  const runNumber = useField("eResponse/eResponse.04");
  const incidentNumber = useField("eResponse/eResponse.03");
  const patientName = [first, last].filter(Boolean).join(" ") || "Unnamed Patient";
  const runLabel = runNumber || incidentNumber || "";

  return (
    <div className="t1">
      <div className="t1-brand">
        <span className="dot" />
        Example ePCR
      </div>
      <button className="t1-home"><Icons.home size={36} /></button>
      <div style={{ position: "relative" }}>
        <button className="t1-patient" onClick={onToggleReport}>
          <span className="label">{runLabel ? "Run " + runLabel : ""}</span>
          <span className="name">
            {patientName} <Icons.chev className="chev" size={28} />
          </span>
        </button>
        {reportOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={onCloseReport} />
            <div className="report-pop">
              <h4>Switch Report</h4>
              {reports.map(r => (
                <button
                  key={r.file}
                  className={r.file === currentReport ? "active" : ""}
                  onClick={() => onSelectReport(r.file)}
                >
                  <span className="report-pop-label">{r.label}</span>
                  <span className="report-pop-sub">{r.sublabel}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {/* <button className="t1-btn" title="Switch report"><Icons.swap size={32} /></button> */}
      <div className="t1-spacer" />
      <div className="t1-actions" style={{ position: "relative" }}>
        {/* <button className="t1-btn" title="Keyboard shortcuts"><Icons.keyboard size={36} /></button> */}
        <div className="t1-btn-wrap">
          <button
            className={"t1-btn" + (validating ? " rapid" : "")}
            title="Validate"
            onClick={onValidate}
          >
            {validating ? <Icons.star4 size={36} /> : <Icons.star4 size={36} />}
          </button>
          {hasViolations && violationCount > 0 && (
            <span className="t1-vbadge">{violationCount > 99 ? '99+' : violationCount}</span>
          )}
        </div>
        <button className="t1-btn" title="Timeline"><Icons.timeline size={36} /></button>
        {/* <button className="t1-btn" title="Capture image"><Icons.camera size={36} /></button> */}
        <button className="t1-btn" title="Menu"><Icons.menu size={36} /></button>
        {/* <button className="t1-btn rapid" title="Rapid actions" onClick={onRapid}>
          <Icons.bolt size={36} />
        </button> */}
        {rapidOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={onCloseRapid} />
            <div className="rapid-pop">
              <h4>Rapid actions</h4>
              <button><Icons.pulse size={28} /> Add vitals</button>
              <button><Icons.flow size={28} /> Repeat last treatment</button>
              <button><Icons.clipboard size={28} /> Reassessment template</button>
              <button><Icons.note size={28} /> Append to narrative</button>
              <button><Icons.alert size={28} /> Flag critical</button>
              <button className="danger"><Icons.alert size={28} /> Mark refused care</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ViewTabs({ current, onSelect }) {
  return (
    <nav className="t2" aria-label="Report sections">
      {VIEWS.map(v => (
        <button
          key={v.id}
          className={"t2-tab" + (current === v.id ? " active" : "")}
          onClick={() => onSelect(v.id)}
        >
          <v.icon size={36} className="icon" />
          <span>{v.label}</span>
        </button>
      ))}
    </nav>
  );
}

function ValidationPanel({ violations, validating, totalCount, onClose, onRerun, onNavigate, specFn,
                            sending, dropdownResults, schematronMutations,
                            rejectedIds, acceptedIds, onAccept, onReject, onAcceptAll, onSendToCadmus }) {
  const [tab, setTab] = React.useState('schematron');
  const fatal   = (violations || []).filter(v => v.role === '[FATAL]');
  const warning = (violations || []).filter(v => v.role !== '[FATAL]');

  const rejectedSet = React.useMemo(() => new Set(rejectedIds || []), [rejectedIds]);
  const acceptedSet = React.useMemo(() => new Set(acceptedIds || []), [acceptedIds]);
  const visibleSchematron = React.useMemo(
    () => (schematronMutations || []).filter(r => !rejectedSet.has(mutationKey(r))),
    [schematronMutations, rejectedSet]
  );
  const visibleClinical = React.useMemo(
    () => (dropdownResults || []).filter(r => !rejectedSet.has(mutationKey(r))),
    [dropdownResults, rejectedSet]
  );
  const actionableCount = React.useMemo(
    () => [...visibleSchematron, ...visibleClinical]
            .filter(r => r.op !== null && r.severity !== '[FATAL]').length,
    [visibleSchematron, visibleClinical]
  );

  return ReactDOM.createPortal(
    <div className="epcr-vp-back" onClick={onClose}>
      <div className="epcr-vp" onClick={e => e.stopPropagation()}>
        <div className="epcr-vp-head">
          <img src="/logo-dark.png" alt="CADMUS" style={{ height: 28, display: "block" }} />
          {violations !== null && !validating && !sending && (
            <span className={"epcr-vp-count" + (totalCount === 0 ? " ok" : "")}>
              {totalCount === 0 ? "All clear" : `${totalCount} issue${totalCount !== 1 ? 's' : ''}`}
            </span>
          )}
          <div className="epcr-vp-actions">
            <button className="epcr-vp-rerun" onClick={e => { e.stopPropagation(); onRerun(); }} disabled={validating || sending}>
              {validating ? "Running…" : "Re-run"}
            </button>
            <button
              className="epcr-vp-send"
              onClick={e => { e.stopPropagation(); onSendToCadmus(); setTab('mutations'); }}
              disabled={validating || sending}
            >
              {sending ? "Sending…" : "Send to CADMUS"}
            </button>
          </div>
          <button className="epcr-vp-close" onClick={onClose}>
            <Icons.close size={32} />
          </button>
        </div>

        <div className="epcr-vp-tabs">
          <button className={"epcr-vp-tab" + (tab === 'schematron' ? " active" : "")} onClick={() => setTab('schematron')}>
            Schematron
          </button>
          {/* <button className={"epcr-vp-tab" + (tab === 'raw' ? " active" : "")} onClick={() => setTab('raw')}>
            Raw
          </button> */}
          <button className={"epcr-vp-tab" + (tab === 'mutations' ? " active" : "")} onClick={() => setTab('mutations')}>
            Suggested Edits
            {(() => {
              const total = (dropdownResults || []).length + (schematronMutations || []).length;
              return (dropdownResults !== null || schematronMutations !== null) && total > 0
                ? <span className="epcr-vp-tab-count">{total}</span>
                : null;
            })()}
          </button>
        </div>

        <div className="epcr-vp-list">
          {tab === 'schematron' && (
            <>
              {validating && <div className="epcr-vp-empty">Running schematron validation…</div>}
              {!validating && violations === null && (
                <div className="epcr-vp-empty">Click Re-run to validate this report against the schematron rules.</div>
              )}
              {!validating && violations !== null && totalCount === 0 && (
                <div className="epcr-vp-empty">No violations found.</div>
              )}
              {!validating && fatal.length > 0 && (
                <>
                  <div className="epcr-vp-group-head">Fatal ({fatal.length})</div>
                  {fatal.map((v, i) => (
                    <ViolationItem key={i} v={v} onNavigate={v => { onNavigate(v); onClose(); }} specFn={specFn} />
                  ))}
                </>
              )}
              {!validating && warning.length > 0 && (
                <>
                  <div className="epcr-vp-group-head">Warning ({warning.length})</div>
                  {warning.map((v, i) => (
                    <ViolationItem key={i} v={v} onNavigate={v => { onNavigate(v); onClose(); }} specFn={specFn} />
                  ))}
                </>
              )}
            </>
          )}

          {tab === 'raw' && (
            <>
              {sending && <div className="epcr-vp-empty">Sending to CADMUS — running full analysis…</div>}
              {!sending && dropdownResults === null && (
                <div className="epcr-vp-empty">Click "Send to CADMUS" to run full clinical analysis.</div>
              )}
              {!sending && dropdownResults !== null && (
                <>
                  {violations !== null && violations.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Schematron ({violations.length})</div>
                      {violations.map((v, i) => (
                        <div key={i} className="epcr-vp-item" style={{ cursor: 'default' }}>
                          <div className="epcr-vp-item-meta">
                            <span className={"epcr-vp-badge " + (v.role === '[FATAL]' ? "fatal" : "warn")}>
                              {v.role === '[FATAL]' ? "Fatal" : "Warning"}
                            </span>
                            {v.element && <span className="epcr-vp-elem">{v.element}</span>}
                          </div>
                          <div className="epcr-vp-msg">{v.message}</div>
                        </div>
                      ))}
                    </>
                  )}
                  {violations !== null && violations.length === 0 && (
                    <div className="epcr-vp-group-head" style={{ color: '#16a34a' }}>Schematron — No violations</div>
                  )}
                  <div className="epcr-vp-group-head">AI Analysis ({dropdownResults.length})</div>
                  {dropdownResults.length === 0 && (
                    <div className="epcr-vp-empty" style={{ padding: '20px 36px' }}>No discrepancies found.</div>
                  )}
                  {dropdownResults.map((r, i) => (
                    <DropdownResultItem key={i} r={r} />
                  ))}
                </>
              )}
            </>
          )}

          {tab === 'mutations' && (
            <>
              {sending && <div className="epcr-vp-empty">Sending to CADMUS — running analysis…</div>}
              {!sending && dropdownResults === null && schematronMutations === null && (
                <div className="epcr-vp-empty">Click "Send to CADMUS" to generate mutations.</div>
              )}
              {!sending && (dropdownResults !== null || schematronMutations !== null) && (
                <>
                  {actionableCount > 0 && (
                    <div className="epcr-mut-bulk">
                      <span>{actionableCount} actionable edit{actionableCount !== 1 ? 's' : ''}</span>
                      <button className="epcr-mut-btn accept" onClick={onAcceptAll}>
                        Accept all edits
                      </button>
                    </div>
                  )}
                  {visibleSchematron.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Schematron Mutations ({visibleSchematron.length})</div>
                      {visibleSchematron.map((r, i) => (
                        <MutationCard key={`sch-${i}`} r={r} isAccepted={acceptedSet.has(mutationKey(r))} onAccept={onAccept} onReject={onReject} />
                      ))}
                    </>
                  )}
                  {visibleClinical.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Clinical Analysis ({visibleClinical.length})</div>
                      {visibleClinical.map((r, i) => (
                        <MutationCard key={`clin-${i}`} r={r} isAccepted={acceptedSet.has(mutationKey(r))} onAccept={onAccept} onReject={onReject} />
                      ))}
                    </>
                  )}
                  {visibleSchematron.length === 0 && visibleClinical.length === 0 && (
                    <div className="epcr-vp-empty">No mutations identified.</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function ViolationItem({ v, onNavigate, specFn }) {
  const isFatal  = v.role === '[FATAL]';
  const spec     = v.element ? specFn(v.element) : null;
  const elemName = spec?.name || '';

  return (
    <button className="epcr-vp-item" onClick={() => onNavigate(v)}>
      <div className="epcr-vp-item-meta">
        <span className={"epcr-vp-badge " + (isFatal ? "fatal" : "warn")}>
          {isFatal ? "Fatal" : "Warning"}
        </span>
        {v.element && <span className="epcr-vp-elem">{v.element}</span>}
        {elemName && <span className="epcr-vp-name">{elemName}</span>}
      </div>
      <div className="epcr-vp-msg">{v.message}</div>
    </button>
  );
}

const FEEDBACK_TARGET_LABELS = {
  'missing-vital': 'Missing Vital',
  'missing-med': 'Missing Medication',
  'missing-procedure': 'Missing Procedure',
  'vitals-discrepancy': 'Vitals Discrepancy',
  'medications-discrepancy': 'Medications Discrepancy',
  'procedure-discrepancy': 'Procedure Discrepancy',
  'schematron': 'Schematron',
};

function DropdownResultItem({ r }) {
  return (
    <div className="epcr-vp-item" style={{ cursor: 'default' }}>
      <div className="epcr-vp-item-meta">
        <span className="epcr-vp-badge warn">
          {FEEDBACK_TARGET_LABELS[r.feedbackTarget] || r.feedbackTarget}
        </span>
        {r.validated && (
          <span className="epcr-vp-badge" style={{ background: '#eef9f0', color: '#16a34a' }}>Validated</span>
        )}
        {r.test_metadata_id && (
          <span className="epcr-vp-elem" style={{ marginLeft: 'auto' }}>#{r.test_metadata_id}</span>
        )}
      </div>
      <div className="epcr-vp-msg" style={{ fontWeight: 500 }}>{r.feedbackText}</div>
      {r.feedbackJustification && (
        <div className="epcr-vp-msg" style={{ color: '#6e6e6e', fontSize: '22px' }}>{r.feedbackJustification}</div>
      )}
    </div>
  );
}

const OP_META = {
  add_group: { label: 'Add Group', bg: 'var(--epcr-blue-soft)',  color: 'var(--epcr-blue)' },
  set:       { label: 'Set',       bg: 'var(--epcr-blue-soft)',  color: 'var(--epcr-blue)' },
  replace:   { label: 'Replace',   bg: '#fff8e1',                color: '#8a6f00' },
  clear:     { label: 'Clear',     bg: 'var(--epcr-red-soft)',   color: 'var(--epcr-red)' },
};

function MutationCard({ r, isAccepted, onAccept, onReject }) {
  const op = OP_META[r.op] || null;
  const isActionable = r.op !== null;
  const isFatal = r.severity === '[FATAL]';

  if (isAccepted) {
    return (
      <div className="epcr-mut-accepted-row">
        <span className="epcr-mut-check">✓</span>
        <span className="epcr-mut-accepted-label">{r.display || r.feedbackText}</span>
        {r.elem && <span className="epcr-vp-elem">{r.elem}</span>}
      </div>
    );
  }

  return (
    <div className="epcr-vp-item epcr-mut-card" style={{ cursor: 'default' }}>
      <div className="epcr-vp-item-meta">
        {op && (
          <span className="epcr-vp-badge" style={{ background: op.bg, color: op.color }}>
            {op.label}
          </span>
        )}
        <span className="epcr-vp-badge warn">
          {FEEDBACK_TARGET_LABELS[r.feedbackTarget] || r.feedbackTarget}
        </span>
        {r.severity && (
          <span className={"epcr-vp-badge " + (isFatal ? 'fatal' : 'warn')}>
            {isFatal ? 'Fatal' : 'Warning'}
          </span>
        )}
        {r.test_metadata_id && (
          <span className="epcr-vp-elem" style={{ marginLeft: 'auto' }}>#{r.test_metadata_id}</span>
        )}
      </div>

      {r.display && (
        <div className="epcr-vp-msg" style={{ fontWeight: 600, fontSize: '26px' }}>{r.display}</div>
      )}
      {r.feedbackText && (
        <div className="epcr-vp-msg" style={{ fontWeight: 500 }}>{r.feedbackText}</div>
      )}
      {r.feedbackJustification && (
        <div className="epcr-vp-msg" style={{ color: '#6e6e6e', fontSize: '22px' }}>{r.feedbackJustification}</div>
      )}

      <div className="epcr-mut-fields">
        {r.elem && (
          <span className="epcr-mut-field">
            <span className="epcr-mut-field-label">elem</span>
            <span className="epcr-vp-elem">{r.elem}</span>
          </span>
        )}
        {r.value !== null && r.value !== undefined && (
          <span className="epcr-mut-field">
            <span className="epcr-mut-field-label">value</span>
            <span className="epcr-vp-elem">{String(r.value)}</span>
          </span>
        )}
      </div>

      {isActionable ? (
        <div className="epcr-mut-actions">
          <button
            className={"epcr-mut-btn accept" + (isFatal ? " fatal-accept" : "")}
            onClick={() => onAccept(r)}
          >
            Accept
          </button>
          <button className="epcr-mut-btn reject" onClick={() => onReject(r)}>
            Reject
          </button>
        </div>
      ) : (
        <div className="epcr-mut-manual">Requires manual review</div>
      )}
    </div>
  );
}
